import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Callout from '@node-space/storybook-components/dist/Callout'
import { Box, Button, DataListItem, Icon } from '@node-space/ui-components'
import { DepositAccountNumberFormats, Options } from 'types/BankAccountsDetails'
import { Currencies, WalletType } from 'types/types'
import { getLocalBankDetails } from 'utils/wallet'
import useDepositPaymentReference from '../hooks/useDepositPaymentReference'

interface LocalDetailsProps {
  options: Options
  wallet: WalletType
  currency: Currencies
  isSegregated: boolean
}

const LocalDetails = ({ options, wallet, currency, isSegregated }: LocalDetailsProps) => {
  const { t } = useTranslation()
  const accountDetails = getLocalBankDetails(options, wallet, currency, isSegregated, t)
  const paymentReference = useDepositPaymentReference(options)

  const isCubixPaymentMethod = options?.accountNumberFormat === DepositAccountNumberFormats.CUBIX

  const accountDetailsText = useMemo(() => {
    const allBeneficiaryBankDetailsText = Object.keys(accountDetails || {})
      .map(label => `${label}: ${accountDetails[label]}`)
      .join('\n')

    return allBeneficiaryBankDetailsText
  }, [accountDetails])

  const handleCopyAllDetails = () => {
    if (accountDetailsText) navigator?.clipboard?.writeText(accountDetailsText)
  }

  if (!Object.keys(accountDetails)?.length) {
    return <Callout state="error" message={t('oopsSomethingWentWrongFetchDespoitDetails')} />
  }

  return (
    <Box>
      {Object.keys(accountDetails)
        .filter(label => !!label)
        .map(label => {
          return (
            <DataListItem
              key={label}
              BodyAvatar={null}
              HeaderHighlight={null}
              Tooltip={null}
              body={accountDetails[label]}
              copyText={accountDetails[label]}
              id={label}
              title={label}
              layout="horizontal"
            />
          )
        })}
      <Box flexCol justify="center" align="center" paddingT={16}>
        <Button
          variant="ghost"
          iconPosition="right"
          iconElement={<Icon name="Copy" />}
          onClick={handleCopyAllDetails}
        >
          {t('copyAllDetails')}
        </Button>
      </Box>

      {!!paymentReference && (
        <Box flexCol gap={16} paddingY={16}>
          <Callout state="warning" message={t('depositInformation')} />
          {isCubixPaymentMethod && <Callout state="info" message={t('cubixRequestInformation')} />}
        </Box>
      )}
    </Box>
  )
}

export default memo(LocalDetails)
